/**
 * CASEWORK RUNS (2026-10-01, Matt: "a level up function to casework that works
 * similarly to drawing a curtain wall/storefront in Revit … the system starts as a
 * single element along that line … that line can be subdivided … the divisions are
 * boards between casework boxes where the boxes can each individually be
 * customized. So it becomes easy to model a wall of cabinets as a simple 'mass' at
 * first and then later go and further refine it.")
 *
 * A RUN is not its own element. It is a set of ordinary casework elements (real
 * cabinets — fronts, styles, countertop auto-join all unchanged) sharing four
 * instance params (all kind 'data', so they never render as form fields and never
 * ride the eyedropper):
 *   runId        — the run's identity, shared by every member
 *   runLengthFt  — the run's total length, shared by every member
 *   runSized     — this box's width was set deliberately (a slice or a typed width)
 *   runMass      — this box draws as a plain schematic mass (no fronts/toe kick);
 *                  true only while the run is a single undivided box
 *
 * The run's LINE is derived from the members' own geometry every time it is laid
 * out — never stored — so Move / Rotate / Mirror / Copy / Array need no run-aware
 * code at all: whatever transform moved the boxes moved the line. All members share
 * one facing (rotationDeg) and one back line; the run's start is the member whose
 * back-left corner sits furthest along local −x. Order along the line is geometric
 * (projection onto local +x), so a mirrored run reads correctly reversed.
 *
 * RESIZING RULE (Matt, 2026-10-01: "sized boxes lock"): a sized box keeps its width;
 * every unsized box shares what's left equally. If every box is sized, the LAST box
 * absorbs the difference so the run always fills its length exactly.
 *
 * v1 scope: straight runs, seam divisions. The data leaves room for the decided
 * follow-ups (corners — blind / lazy susan / diagonal per corner; fillers and
 * finished panels at any division; stacked tiers) — see docs/MODELER.md
 * "CASEWORK RUNS".
 */
import { reseatStudioFixtures } from './caseworkFollow'
import * as Y from 'yjs'
import type { ElementId, LevelId, ParamValue, PhaseId, Pt2, TypeId } from '../types'
import { newElementId, newNodeId } from '../ids'
import { getElement, getType, listElements, maps, recordToYMap, transact, type ModelDoc } from '../doc'
import type { ElementRecord } from '../types'
import { styleMapOf } from '../caseworkStyle'
import { rowOfParams, rowBays, TALL_DEFAULT_SPAN } from '../caseworkRows'

/** a box can't be sliced narrower than the casework schema's own minimum width */
export const RUN_MIN_BOX_FT = 0.75

export interface RunMember {
  id: ElementId
  /** back-center anchor */
  anchor: Pt2
  widthFt: number
  sized: boolean
  /** CASEWORK STUDIO divisions: a filler strip or finished panel standing in the run (never a
   *  cabinet; always keeps its width, never absorbs a stretch, can't be sliced) */
  part?: 'filler' | 'panel' | null
  /** where the box starts along the casework, from its start (BAYS AND LAYERS: an upper layer
   *  has gaps where tall boxes stand, so a member's place is its own, not a running sum) */
  s0?: number
  /** the box's style (base / upper / tall / closet — its type's, or its own override) */
  style?: string
  /** CASEWORK ROWS (bim-plan/36): the row the box starts in and how many rows it covers */
  row?: number
  span?: number
}

/**
 * BAYS AND LAYERS (2026-10-09, Matt: "do the whole run of cabinetry inclusive of the uppers and
 * floating shelves … a tall cabinet could span across a horizontal grid"). One casework line
 * holds two LAYERS along the same back line: the FLOOR layer (base and tall boxes, end to end —
 * the line every existing tool works on) and the UPPER layer (upper cabinets, floating shelves,
 * open wall). Tall boxes split the line into BAYS; the upper layer fills each bay on its own and
 * never crosses a tall box. A casework with no floor boxes (uppers drawn on their own) is an
 * upper line by itself, exactly as before. A box's layer follows its style.
 */
export type RunLayer = 'floor' | 'upper' | `row${number}`

/** CASEWORK ROWS (bim-plan/36): the layers are rows — row 0 'floor', row 1 'upper', row n 'row<n>' */
export const layerOfRow = (row: number): RunLayer => (row <= 0 ? 'floor' : row === 1 ? 'upper' : `row${row}`)
export const rowOfLayer = (layer: RunLayer): number => (layer === 'floor' ? 0 : layer === 'upper' ? 1 : Number(layer.slice(3)) || 0)

/** a casework box's style: its own override, else its type's */
export function memberStyle(model: ModelDoc, rec: ElementRecord): string {
  const box = rec.params.boxStyle as unknown as Record<string, unknown> | undefined
  const own = box && typeof box === 'object' && typeof box.style === 'string' ? box.style : null
  return own || String(getType(model, rec.typeId)?.params.style || 'base')
}
export const layerOfStyle = (style: string): RunLayer => (style === 'upper' ? 'upper' : 'floor')
/** a box's row and span — its own stored ones, else derived from its style (caseworkRows.ts) */
export function memberRow(model: ModelDoc, rec: ElementRecord): { row: number; span: number } {
  return rowOfParams(rec.params as Record<string, unknown>, memberStyle(model, rec))
}
export function memberLayer(model: ModelDoc, rec: ElementRecord): RunLayer {
  return layerOfRow(memberRow(model, rec).row)
}
/** the highest row a run has a box in (0 when it is only a floor line) */
export function runTopRow(model: ModelDoc, runId: string): number {
  let top = 0
  for (const el of listElements(model)) {
    if (el.category !== 'casework' || el.params.runId !== runId) continue
    top = Math.max(top, memberRow(model, el).row)
  }
  return top
}

export interface RunFrame {
  runId: string
  /** run start: back-left corner of the first box */
  start: Pt2
  /** unit vector along the run (the boxes' local +x) */
  dir: Pt2
  /** unit vector toward the fronts (the boxes' local +y) */
  front: Pt2
  lengthFt: number
  rotationDeg: number
  /** members in order along the run, with their CURRENT stored widths (the layer asked for) */
  members: RunMember[]
  /** the layer `members` holds, and the casework's PRIMARY layer (floor, unless it has only uppers) */
  layer: RunLayer
  primary: RunLayer
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** the run a casework element belongs to, or null */
export function runIdOf(model: ModelDoc, id: ElementId): string | null {
  const rec = getElement(model, id)
  if (!rec || rec.category !== 'casework') return null
  const r = rec.params.runId
  return typeof r === 'string' && r ? r : null
}

/** every element id in the run */
export function runMemberIds(model: ModelDoc, runId: string): ElementId[] {
  return listElements(model)
    .filter((el) => el.category === 'casework' && el.params.runId === runId)
    .map((el) => el.id)
}

/** expand a selection so every touched run is selected whole (de-duplicated, order kept) */
export function expandRuns(model: ModelDoc, ids: ElementId[]): ElementId[] {
  const out: ElementId[] = []
  const seen = new Set<ElementId>()
  const push = (id: ElementId) => { if (!seen.has(id)) { seen.add(id); out.push(id) } }
  for (const id of ids) {
    const r = runIdOf(model, id)
    if (!r) { push(id); continue }
    for (const m of runMemberIds(model, r)) push(m)
  }
  return out
}

/** derive the run's line + ordered members from the members' current geometry. `layer` picks
 *  which layer's members come back (default: the PRIMARY layer); the line itself (start,
 *  length) is always the primary layer's */
export function runFrame(model: ModelDoc, runId: string, layer?: RunLayer): RunFrame | null {
  const all = listElements(model).filter((el) => el.category === 'casework' && el.params.runId === runId)
  if (!all.length) return null
  const layerOf = new Map(all.map((el) => [el.id, memberLayer(model, el)] as const))
  const primary: RunLayer = layerOfRow(Math.min(...all.map((el) => rowOfLayer(layerOf.get(el.id)!))))
  const want = layer ?? primary
  const recs = all.filter((el) => layerOf.get(el.id) === primary)
  const rot = isNum(recs[0].params.rotationDeg) ? recs[0].params.rotationDeg : 0
  const r = (rot * Math.PI) / 180
  const dir: Pt2 = [Math.cos(r), Math.sin(r)]
  const front: Pt2 = [-Math.sin(r), Math.cos(r)]
  const members: (RunMember & { s0: number })[] = []
  let back = 0
  let lengthFt = 0
  for (const el of recs) {
    const pt = (el.params.path as { nodes?: { pt: Pt2 }[] } | null)?.nodes?.[0]?.pt
    if (!pt) continue
    const w = isNum(el.params.widthFt) && el.params.widthFt > 0 ? el.params.widthFt : 2
    const s = pt[0] * dir[0] + pt[1] * dir[1]
    const part = el.params.runPart === 'filler' || el.params.runPart === 'panel' ? el.params.runPart : null
    members.push({ id: el.id, anchor: [pt[0], pt[1]], widthFt: w, sized: el.params.runSized === true || !!part, part, s0: s - w / 2 })
    if (members.length === 1) back = pt[0] * front[0] + pt[1] * front[1]
    if (isNum(el.params.runLengthFt) && el.params.runLengthFt > lengthFt) lengthFt = el.params.runLengthFt
  }
  if (!members.length) return null
  members.sort((a, b) => a.s0 - b.s0)
  const s0 = members[0].s0
  if (!(lengthFt > 0)) lengthFt = members.reduce((t, m) => t + m.widthFt, 0)
  const start: Pt2 = [dir[0] * s0 + front[0] * back, dir[1] * s0 + front[1] * back]
  const withStyle = (m: RunMember & { s0: number }): RunMember => {
    const rec = getElement(model, m.id)!
    const { row, span } = memberRow(model, rec)
    return { ...m, s0: m.s0 - s0, style: memberStyle(model, rec), row, span }
  }
  let out: RunMember[]
  if (want === primary) out = members.map(withStyle)
  else {
    const other: (RunMember & { s0: number })[] = []
    for (const el of all) {
      if (layerOf.get(el.id) !== want) continue
      const pt = (el.params.path as { nodes?: { pt: Pt2 }[] } | null)?.nodes?.[0]?.pt
      if (!pt) continue
      const w = isNum(el.params.widthFt) && el.params.widthFt > 0 ? el.params.widthFt : 2
      const s = pt[0] * dir[0] + pt[1] * dir[1]
      const part = el.params.runPart === 'filler' || el.params.runPart === 'panel' ? el.params.runPart : null
      other.push({ id: el.id, anchor: [pt[0], pt[1]], widthFt: w, sized: el.params.runSized === true || !!part, part, s0: s - w / 2 })
    }
    other.sort((a, b) => a.s0 - b.s0)
    out = other.map(withStyle)
  }
  return { runId, start, dir, front, lengthFt, rotationDeg: rot, members: out, layer: want, primary }
}

/**
 * The upper layer's BAYS: the stretches of the line no tall box stands in, as [s0, s1] from
 * the casework's start, given the primary (floor) layer's laid-out boxes.
 */
export function upperBays(floor: { widthFt: number; style?: string; span?: number }[], lengthFt: number): [number, number][] {
  // CASEWORK ROWS: the bays of row 1, from the floor row's boxes and how far each reaches
  let s = 0
  const boxes = floor.map((m) => {
    const b = { s0: s, s1: s + m.widthFt, row: 0, span: m.span ?? (m.style === 'tall' ? TALL_DEFAULT_SPAN : 1) }
    s += m.widthFt
    return b
  })
  return rowBays(boxes, lengthFt, 1)
}

/**
 * The resizing rule, pure: sized boxes keep their width, unsized share the rest; all
 * sized → the END box absorbs (the last one, or the first when the run's START end is
 * the one being dragged — `absorbFirst`). Exported for tests and the slice preview.
 */
export function solveRunWidths(
  lengthFt: number, members: { widthFt: number; sized: boolean; part?: string | null }[], absorbFirst = false,
): number[] {
  const n = members.length
  if (!n) return []
  const flex = members.filter((m) => !m.sized).length
  const sizedSum = members.reduce((t, m) => t + (m.sized ? m.widthFt : 0), 0)
  if (flex > 0) {
    const each = Math.max(RUN_MIN_BOX_FT, (lengthFt - sizedSum) / flex)
    return members.map((m) => (m.sized ? m.widthFt : each))
  }
  const widths = members.map((m) => m.widthFt)
  const k = absorbIndex(members, absorbFirst)
  const rest = lengthFt - (sizedSum - widths[k])
  widths[k] = Math.max(RUN_MIN_BOX_FT, rest)
  return widths
}

/**
 * The shortest the run can get without breaking the resizing rule: sized boxes keep
 * their widths, every flexing box keeps at least RUN_MIN_BOX_FT.
 */
/** which member absorbs when every member is sized: the end box — never a filler or panel */
function absorbIndex(members: { part?: string | null }[], absorbFirst: boolean): number {
  const n = members.length
  if (absorbFirst) { for (let i = 0; i < n; i++) if (!members[i].part) return i; return 0 }
  for (let i = n - 1; i >= 0; i--) if (!members[i].part) return i
  return n - 1
}

export function runMinLengthFt(members: { widthFt: number; sized: boolean; part?: string | null }[], absorbFirst = false): number {
  const n = members.length
  if (!n) return 0
  const flex = members.filter((m) => !m.sized).length
  const sizedSum = members.reduce((t, m) => t + (m.sized ? m.widthFt : 0), 0)
  if (flex > 0) return sizedSum + flex * RUN_MIN_BOX_FT
  const k = absorbIndex(members, absorbFirst)
  return sizedSum - members[k].widthFt + RUN_MIN_BOX_FT
}

/**
 * Re-lay the run along its line: widths by the resizing rule, boxes placed end to
 * end from the start, every member sharing the run's facing and length. Idempotent;
 * returns false if the run is gone.
 */
export function layoutRun(
  model: ModelDoc, runId: string,
  overrides?: { lengthFt?: number; startShiftFt?: number; absorbFirst?: boolean },
): boolean {
  const f = runFrame(model, runId)
  if (!f) return false
  const absorbFirst = overrides?.absorbFirst === true
  const lengthFt = Math.max(overrides?.lengthFt ?? f.lengthFt, runMinLengthFt(f.members, absorbFirst))
  const widths = solveRunWidths(lengthFt, f.members, absorbFirst)
  const sh = overrides?.startShiftFt ?? 0
  const start: Pt2 = [f.start[0] + f.dir[0] * sh, f.start[1] + f.dir[1] * sh]
  const { elements } = maps(model)
  transact(model, () => {
    let s = 0
    f.members.forEach((m, i) => {
      const w = widths[i]
      const c = s + w / 2
      const pt: Pt2 = [start[0] + f.dir[0] * c, start[1] + f.dir[1] * c]
      s += w
      const pm = elements.get(m.id)?.get('params') as Y.Map<unknown> | undefined
      if (!pm) return
      const path = pm.get('path') as { closed: boolean; nodes: { id: string; pt: Pt2 }[] } | undefined
      const nodeId = path?.nodes?.[0]?.id || newNodeId()
      const old = path?.nodes?.[0]?.pt
      if (!old || Math.abs(old[0] - pt[0]) > 1e-9 || Math.abs(old[1] - pt[1]) > 1e-9) {
        pm.set('path', { closed: false, nodes: [{ id: nodeId, pt }] })
      }
      if (pm.get('widthFt') !== w) pm.set('widthFt', w)
      if (pm.get('rotationDeg') !== f.rotationDeg) pm.set('rotationDeg', f.rotationDeg)
      if (pm.get('runLengthFt') !== lengthFt) pm.set('runLengthFt', lengthFt)
    })
  })
  // BAYS AND LAYERS → CASEWORK ROWS (bim-plan/36): each row above the floor fills the bays no
  // lower box reaches into, row by row from the bottom (with today's two rows: the uppers fill
  // the bays the tall boxes leave, exactly as before)
  const upperIds: ElementId[] = []
  if (f.primary === 'floor') {
    // the boxes laid so far, by their stretch along the run — what the rows above give way to
    const laid: { s0: number; s1: number; row: number; span: number }[] = []
    let acc = 0
    f.members.forEach((m, i) => { laid.push({ s0: acc, s1: acc + widths[i], row: m.row ?? 0, span: m.span ?? 1 }); acc += widths[i] })
    const top = runTopRow(model, runId)
    for (let r = 1; r <= top; r++) {
      const up = runFrame(model, runId, layerOfRow(r))
      if (!up || !up.members.length || up.layer !== layerOfRow(r)) continue
      upperIds.push(...up.members.map((m) => m.id))
      const bays = rowBays(laid, lengthFt, r)
      // each box goes with the bay its middle is in (or the nearest one)
      const byBay = new Map<number, RunMember[]>()
      for (const m of up.members) {
        const c = (m.s0 ?? 0) + m.widthFt / 2 - sh
        let bi = bays.findIndex(([a, b]) => c >= a - 1e-6 && c <= b + 1e-6)
        if (bi < 0 && bays.length) {
          let best = Infinity
          bays.forEach(([a, b], k) => { const dd = Math.min(Math.abs(c - a), Math.abs(c - b)); if (dd < best) { best = dd; bi = k } })
        }
        if (bi < 0) continue
        const arr = byBay.get(bi) ?? []
        arr.push(m)
        byBay.set(bi, arr)
      }
      transact(model, () => {
        for (const [bi, ms] of byBay) {
          const [a, b] = bays[bi]
          const ws = solveRunWidths(b - a, ms)
          let s = a
          ms.forEach((m, i) => {
            const w = ws[i]
            const c = s + w / 2
            laid.push({ s0: s, s1: s + w, row: m.row ?? r, span: m.span ?? 1 })
            s += w
            const pt: Pt2 = [start[0] + f.dir[0] * c, start[1] + f.dir[1] * c]
            const pm = elements.get(m.id)?.get('params') as Y.Map<unknown> | undefined
            if (!pm) return
            const path = pm.get('path') as { closed: boolean; nodes: { id: string; pt: Pt2 }[] } | undefined
            const old = path?.nodes?.[0]?.pt
            if (!old || Math.abs(old[0] - pt[0]) > 1e-9 || Math.abs(old[1] - pt[1]) > 1e-9) {
              pm.set('path', { closed: false, nodes: [{ id: path?.nodes?.[0]?.id || newNodeId(), pt }] })
            }
            if (pm.get('widthFt') !== w) pm.set('widthFt', w)
            if (pm.get('rotationDeg') !== f.rotationDeg) pm.set('rotationDeg', f.rotationDeg)
            if (pm.get('runLengthFt') !== lengthFt) pm.set('runLengthFt', lengthFt)
          })
        }
      })
    }
  }
  // CASEWORK STUDIO: the fixtures placed on these boxes follow them
  reseatStudioFixtures(model, [...f.members.map((m) => m.id), ...upperIds])
  return true
}

/**
 * Draw a run: one casework element filling the line a→b as a plain mass. The fronts
 * face the LEFT of travel (rotationDeg 0 faces +y with local +x = travel), `flip`
 * puts them on the right. Returns the member id.
 */
export function createCaseworkRun(
  model: ModelDoc,
  input: {
    typeId: TypeId
    levelId: LevelId
    a: Pt2
    b: Pt2
    flip?: boolean
    phaseCreated?: PhaseId
    /** extra instance params (picked-up settings, a height override) */
    params?: Record<string, ParamValue>
  },
): ElementId | null {
  const dx = input.b[0] - input.a[0], dy = input.b[1] - input.a[1]
  const L = Math.hypot(dx, dy)
  if (L < RUN_MIN_BOX_FT) return null
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI
  if (input.flip) deg += 180
  const mid: Pt2 = [(input.a[0] + input.b[0]) / 2, (input.a[1] + input.b[1]) / 2]
  const id = newElementId()
  const runId = newElementId()
  transact(model, () => {
    maps(model).elements.set(id, recordToYMap({
      id, category: 'casework', typeId: input.typeId, levelId: input.levelId,
      ...(input.phaseCreated ? { phaseCreated: input.phaseCreated } : {}),
      params: {
        ...(input.params || {}),
        path: { closed: false, nodes: [{ id: newNodeId(), pt: mid }] },
        rotationDeg: deg,
        widthFt: L,
        runId, runLengthFt: L, runSized: false, runMass: true,
      },
    }))
  })
  return id
}

/** distance along the run of a world point (clamped to the run) */
export function runStationOf(f: RunFrame, pt: Pt2): number {
  const s = (pt[0] - f.start[0]) * f.dir[0] + (pt[1] - f.start[1]) * f.dir[1]
  return Math.max(0, Math.min(f.lengthFt, s))
}

/** where each box starts/ends along the run, by its CURRENT stored widths (a layer with gaps —
 *  the uppers between tall boxes — uses each box's own place) */
export function runBoxSpans(f: RunFrame): { id: ElementId; s0: number; s1: number; part?: string | null }[] {
  let s = 0
  return f.members.map((m) => {
    const s0 = f.layer !== f.primary && m.s0 != null ? m.s0 : s
    s = s0 + m.widthFt
    return { id: m.id, s0, s1: s, part: m.part ?? null }
  })
}

export interface SliceTarget {
  /** the box the slice falls in */
  id: ElementId
  s0: number
  s1: number
  /** the slice station, along the run */
  s: number
  /** true when the slice would leave a box narrower than RUN_MIN_BOX_FT, or sits on an existing seam */
  invalid: boolean
}

/** which box a slice at station `s` would split (null outside the run) */
export function sliceTargetAt(f: RunFrame, s: number): SliceTarget | null {
  const spans = runBoxSpans(f)
  for (const sp of spans) {
    if (s < sp.s0 - 1e-9 || s > sp.s1 + 1e-9) continue
    const invalid = !!sp.part || s - sp.s0 < RUN_MIN_BOX_FT - 1e-9 || sp.s1 - s < RUN_MIN_BOX_FT - 1e-9
    return { id: sp.id, s0: sp.s0, s1: sp.s1, s, invalid }
  }
  return null
}

/**
 * SLICE: split the box under station `s` into two at that point — the Revit curtain
 * grid line. The left part keeps the original element (and every customization on
 * it), now SIZED to its new width; the right part is a copy of it, sized only if the
 * original was. Any slice ends the run's massing phase: every member grows fronts.
 * One undo step. Returns the new box's id.
 */
export function sliceRun(model: ModelDoc, runId: string, s: number, layer?: RunLayer): ElementId | null {
  const f = runFrame(model, runId, layer)
  if (!f) return null
  const t = sliceTargetAt(f, s)
  if (!t || t.invalid) return null
  const rec = getElement(model, t.id)
  const src = maps(model).elements.get(t.id)
  if (!rec || !src) return null
  const leftW = s - t.s0
  const rightW = t.s1 - s
  const at = (c: number): Pt2 => [f.start[0] + f.dir[0] * c, f.start[1] + f.dir[1] * c]
  const nid = newElementId()
  transact(model, () => {
    const { elements } = maps(model)
    // the new right-hand box: a full copy of the source's settings (every
    // customization rides along), placed just past the slice so the geometric
    // ordering lands it right of the source
    const params = JSON.parse(JSON.stringify(rec.params ?? {})) as Record<string, ParamValue>
    delete params.pinned
    elements.set(nid, recordToYMap({
      id: nid, category: rec.category, typeId: rec.typeId, levelId: rec.levelId,
      ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
      ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
      params: {
        ...params,
        path: { closed: false, nodes: [{ id: newNodeId(), pt: at(s + rightW / 2) }] },
        widthFt: rightW,
        runSized: rec.params.runSized === true,
      },
    }))
    // the source becomes the left part, sized to the slice
    const spm = src.get('params') as Y.Map<unknown>
    const node = (spm.get('path') as { nodes?: { id: string }[] } | undefined)?.nodes?.[0]
    spm.set('path', { closed: false, nodes: [{ id: node?.id || newNodeId(), pt: at(t.s0 + leftW / 2) }] })
    spm.set('widthFt', leftW)
    spm.set('runSized', true)
    // massing ends at the first division — every box in the run shows its fronts
    for (const m of runMemberIds(model, runId)) {
      const mp = elements.get(m)?.get('params') as Y.Map<unknown> | undefined
      if (mp?.get('runMass') === true) mp.set('runMass', false)
    }
    layoutRun(model, runId)
  })
  return nid
}

/** set the run's total length (the start stays put; boxes re-lay by the resizing rule) */
export function setRunLength(model: ModelDoc, runId: string, lengthFt: number): boolean {
  return setRunEndLength(model, runId, 'end', lengthFt) !== null
}

/**
 * RUN END GRIPS (2026-10-01, Matt: "extend or retract it in plan, elevation, and 3D"):
 * drag one END of the run — the other end holds. `end: 'end'` moves the far end (the
 * last box absorbs when every box is sized); `end: 'start'` moves the start (the
 * first box absorbs). The length is clamped to what the sized boxes need. Returns the
 * length actually applied, or null.
 */
export function setRunEndLength(model: ModelDoc, runId: string, end: 'start' | 'end', lengthFt: number): number | null {
  const f = runFrame(model, runId)
  if (!f || !(lengthFt > 0)) return null
  const absorbFirst = end === 'start'
  const L = Math.max(lengthFt, runMinLengthFt(f.members, absorbFirst))
  const ok = layoutRun(model, runId, {
    lengthFt: L, absorbFirst, startShiftFt: end === 'start' ? f.lengthFt - L : 0,
  })
  return ok ? L : null
}

/** the two ends of the run's back line (the line it was drawn along) */
export function runEndPoints(f: RunFrame): { start: Pt2; end: Pt2 } {
  return { start: [f.start[0], f.start[1]], end: [f.start[0] + f.dir[0] * f.lengthFt, f.start[1] + f.dir[1] * f.lengthFt] }
}

/**
 * RUN ENDS ARE THE DRAWN LINE (2026-10-19, Matt: "I would rather it work like clicking and
 * dragging the lines … as when I place it in the first place I run it along the wall … if the
 * wall moves or rotates, it's easy for me to align it and change the length"): move one END of
 * the back line to `pt` — anywhere, like a wall's end. The other end holds; the casework turns
 * to run between them (every box, both layers, keeps its place along the line and its facing
 * relative to it) and re-lays at the new length by the resizing rule. One undo step. Returns
 * the length applied, or null (shorter than one box can be).
 */
export function setRunEndPoint(model: ModelDoc, runId: string, end: 'start' | 'end', pt: Pt2): number | null {
  const f = runFrame(model, runId)
  if (!f) return null
  const ends = runEndPoints(f)
  const fixed = end === 'end' ? ends.start : ends.end
  // travel runs start → end, so the new direction is fixed → pt for the far end, pt → fixed for the start
  const v: Pt2 = end === 'end' ? [pt[0] - fixed[0], pt[1] - fixed[1]] : [fixed[0] - pt[0], fixed[1] - pt[1]]
  const L = Math.hypot(v[0], v[1])
  if (L < RUN_MIN_BOX_FT) return null
  let dRad = Math.atan2(v[1], v[0]) - Math.atan2(f.dir[1], f.dir[0])
  while (dRad > Math.PI) dRad -= 2 * Math.PI
  while (dRad <= -Math.PI) dRad += 2 * Math.PI
  let out: number | null = null
  transact(model, () => {
    if (Math.abs(dRad) > 1e-9) {
      const c = Math.cos(dRad), sn = Math.sin(dRad)
      const dDeg = (dRad * 180) / Math.PI
      const { elements } = maps(model)
      for (const id of runMemberIds(model, runId)) {
        const pm = elements.get(id)?.get('params') as Y.Map<unknown> | undefined
        if (!pm) continue
        const path = pm.get('path') as { closed: boolean; nodes: { id: string; pt: Pt2 }[] } | undefined
        const p0 = path?.nodes?.[0]?.pt
        if (p0) {
          const dx = p0[0] - fixed[0], dy = p0[1] - fixed[1]
          const np: Pt2 = [fixed[0] + dx * c - dy * sn, fixed[1] + dx * sn + dy * c]
          pm.set('path', { closed: false, nodes: [{ id: path!.nodes[0].id || newNodeId(), pt: np }] })
        }
        const rot = isNum(pm.get('rotationDeg')) ? (pm.get('rotationDeg') as number) : 0
        let deg = rot + dDeg
        while (deg > 180) deg -= 360
        while (deg <= -180) deg += 360
        pm.set('rotationDeg', deg)
      }
    }
    out = setRunEndLength(model, runId, end, L)
  })
  return out
}

/** set one box's width deliberately (it becomes sized); the run's length is held */
export function setRunBoxWidth(model: ModelDoc, id: ElementId, widthFt: number): boolean {
  const runId = runIdOf(model, id)
  const pm = maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined
  if (!runId || !pm) return false
  const w = Math.max(RUN_MIN_BOX_FT, widthFt)
  // TYPED END BOX (2026-10-23): when every other box keeps its width, the box that would absorb
  // the change IS this one — the edit could only snap back. The casework grows or shrinks by the
  // difference instead (its start stays put).
  const f = runFrame(model, runId)
  let lengthFt: number | undefined
  if (f && f.layer === f.primary) {
    const i = f.members.findIndex((m) => m.id === id)
    const others = f.members.filter((m) => m.id !== id)
    if (i >= 0 && others.every((m) => m.sized) && absorbIndex(f.members.map((m) => (m.id === id ? { ...m, sized: true } : m)), false) === i) {
      lengthFt = f.lengthFt + (w - f.members[i].widthFt)
    }
  }
  transact(model, () => {
    pm.set('widthFt', w)
    pm.set('runSized', true)
    layoutRun(model, runId, lengthFt != null ? { lengthFt } : undefined)
  })
  return true
}

/** release a box's width back to the shared remainder */
export function unsizeRunBox(model: ModelDoc, id: ElementId): boolean {
  const runId = runIdOf(model, id)
  const pm = maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined
  if (!runId || !pm) return false
  transact(model, () => {
    pm.set('runSized', false)
    layoutRun(model, runId)
  })
  return true
}

/**
 * Remove the seam on a box's RIGHT side: the box absorbs its right-hand neighbour
 * (the neighbour is deleted). The surviving box keeps its own settings. Returns false
 * when there is no neighbour to the right.
 */
export function mergeRunBoxRight(model: ModelDoc, id: ElementId): boolean {
  const runId = runIdOf(model, id)
  const rec = getElement(model, id)
  if (!runId || !rec) return false
  const f = runFrame(model, runId, memberLayer(model, rec))
  if (!f) return false
  const i = f.members.findIndex((m) => m.id === id)
  if (i < 0 || i + 1 >= f.members.length) return false
  const right = f.members[i + 1]
  const keep = f.members[i]
  // across a tall box the uppers aren't neighbours
  if (f.layer !== f.primary && Math.abs((right.s0 ?? 0) - ((keep.s0 ?? 0) + keep.widthFt)) > 1e-3) return false
  transact(model, () => {
    const { elements } = maps(model)
    const pm = elements.get(id)?.get('params') as Y.Map<unknown> | undefined
    pm?.set('widthFt', keep.widthFt + right.widthFt)
    pm?.set('runSized', keep.sized || right.sized)
    elements.delete(right.id)
    layoutRun(model, runId)
  })
  return true
}

/** which height group a box belongs to: talls, uppers, or everything else on the floor */
export type RunHeightKind = 'base' | 'tall' | 'upper'
export const heightKindOfStyle = (style: string): RunHeightKind => (style === 'tall' ? 'tall' : style === 'upper' ? 'upper' : 'base')

/**
 * Set one KIND of box in the run to one height (null = back to the type's height). Base boxes,
 * tall boxes and uppers each have their own height (2026-10-21, Matt: tall cabinets to 7'-11"
 * and bases to 30" on Grabowski — the height used to skip talls whichever box was picked).
 */
export function setRunHeight(model: ModelDoc, runId: string, heightFt: number | null, kind: RunHeightKind = 'base', row?: number): number {
  const ids = listElements(model)
    .filter((el) => el.category === 'casework' && el.params.runId === runId && heightKindOfStyle(memberStyle(model, el)) === kind)
    // CASEWORK ROWS: an upper's height is its own row's (the rows stacked on it keep theirs)
    .filter((el) => kind !== 'upper' || row == null || memberRow(model, el).row === row)
    // an open wall or floating shelves in the upper layer keep their own span
    .filter((el) => kind !== 'upper' || !['open', 'shelves'].includes(String(styleMapOf(el.params.boxStyle).upperCell ?? getType(model, el.typeId)?.params.upperCell ?? '')))
    .map((el) => el.id)
  transact(model, () => {
    for (const m of ids) {
      const pm = maps(model).elements.get(m)?.get('params') as Y.Map<unknown> | undefined
      pm?.set('heightFt', heightFt)
    }
  })
  return ids.length
}

/**
 * Detach a box from its run: it becomes an ordinary freestanding cabinet where it
 * stands; the rest of the run closes the gap by the resizing rule.
 */
export function detachFromRun(model: ModelDoc, id: ElementId): boolean {
  const runId = runIdOf(model, id)
  const pm = maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined
  if (!runId || !pm) return false
  transact(model, () => {
    for (const k of ['runId', 'runLengthFt', 'runSized', 'runMass']) pm.delete(k)
    const rest = runMemberIds(model, runId)
    if (rest.length) {
      const f = runFrame(model, runId)
      if (f) layoutRun(model, runId, { lengthFt: f.members.reduce((t, m) => t + m.widthFt, 0) })
    }
  })
  return true
}

/**
 * Called after a member is deleted (deleteElement): the surviving boxes close the gap
 * — unsized boxes absorb it, else the last box. A run whose members are all gone has
 * nothing left to do.
 */
export function healRunAfterDelete(model: ModelDoc, runId: string): void {
  if (!runMemberIds(model, runId).length) return
  layoutRun(model, runId)
}

/**
 * Copies of run members (duplicateElement) must never share the source's run — a
 * shared runId would pull the copies back onto the original line at the next layout.
 * The batch transforms call this with every original→copy pair they made: copies of
 * the SAME source run land in one fresh run together; a lone copied box becomes a
 * run of one.
 */
export function remapCopiedRuns(model: ModelDoc, pairs: [ElementId, ElementId][]): void {
  const fresh = new Map<string, string>()
  transact(model, () => {
    for (const [orig, copy] of pairs) {
      const r = runIdOf(model, orig)
      if (!r) continue
      let nr = fresh.get(r)
      if (!nr) { nr = newElementId(); fresh.set(r, nr) }
      const pm = maps(model).elements.get(copy)?.get('params') as Y.Map<unknown> | undefined
      pm?.set('runId', nr)
    }
    // a copied run's length is what was actually copied (a partial copy is shorter)
    for (const nr of fresh.values()) {
      const ids = runMemberIds(model, nr)
      const total = ids.reduce((t, m) => {
        const w = maps(model).elements.get(m)?.get('params') as Y.Map<unknown> | undefined
        const v = w?.get('widthFt')
        return t + (isNum(v) ? v : 0)
      }, 0)
      for (const m of ids) (maps(model).elements.get(m)?.get('params') as Y.Map<unknown> | undefined)?.set('runLengthFt', total)
    }
  })
}

/**
 * duplicateElement's hook: a copied run member starts life as its OWN run of one
 * (never sharing the source's run). Batch copies re-unify via remapCopiedRuns.
 */
export function detachCopyIntoOwnRun(params: Record<string, ParamValue>): void {
  if (typeof params.runId !== 'string' || !params.runId) return
  params.runId = newElementId()
  if (isNum(params.widthFt)) params.runLengthFt = params.widthFt
}
